import { useEffect, useMemo, useState } from "react";
import CloseIcon from "../../../assets/crossIcon.svg";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const hasRejectionReason = (document) => {
  const currentReason = document?.rejectionReason?.trim();

  const historyReasons = Array.isArray(document?.history?.rejectionReason)
    ? document.history.rejectionReason.filter((item) =>
        item?.rejectionReason?.trim(),
      )
    : [];

  return Boolean(currentReason || historyReasons.length);
};

const getRejectionReasons = (document) => {
  const historyReasons = Array.isArray(document?.history?.rejectionReason)
    ? document.history.rejectionReason
        .filter((item) => item?.rejectionReason?.trim())
        .sort(
          (a, b) =>
            new Date(b?.rejectionDate || 0) - new Date(a?.rejectionDate || 0),
        )
    : [];

  if (document?.rejectionReason?.trim()) {
    const currentReason = document.rejectionReason.trim();

    const alreadyExists = historyReasons.some(
      (item) => item?.rejectionReason?.trim() === currentReason,
    );

    if (!alreadyExists) {
      historyReasons.push({
        rejectionReason: currentReason,
        rejectionDate: document?.updatedAt,
      });
    }
  }

  return historyReasons.sort(
    (a, b) => new Date(b?.rejectionDate || 0) - new Date(a?.rejectionDate || 0),
  );
};

const formatDate = (date) => {
  if (!date) return "-";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return "-";
  }

  return parsedDate.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ReferenceRejectionHistoryModal = ({ open, onClose, documents = [] }) => {
  const [currentPage, setCurrentPage] = useState(0);

  const rejectedDocuments = useMemo(() => {
    return documents.filter((document) => hasRejectionReason(document));
  }, [documents]);

  useEffect(() => {
    if (open) {
      setCurrentPage(0);
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const currentDocument = rejectedDocuments[currentPage];

  const rejectionReasons = currentDocument
    ? getRejectionReasons(currentDocument)
    : [];

  const hasPrevious = currentPage > 0;
  const hasNext = currentPage < rejectedDocuments.length - 1;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
      <div className="relative flex h-[70%] w-[85%] max-w-[1100px] flex-col rounded-[20px] bg-white p-6 shadow-xl">
        {/* Header */}
        <div className="relative flex items-center justify-center border-b border-[#E4E4E4] pb-4">
          <h2 className="font-lufga text-[22px] font-medium text-[#000000]">
            Rejection Reason History
          </h2>

          <button
            type="button"
            onClick={onClose}
            className="absolute right-0 top-0 border-0 bg-transparent p-0 outline-none shadow-none"
          >
            <img src={CloseIcon} alt="Close" className="h-5 w-5" />
          </button>
        </div>

        {rejectedDocuments.length === 0 ? (
          <div className="flex flex-1 items-center justify-center">
            <p className="font-lufga text-[16px] text-[#666666]">
              No rejection reason available.
            </p>
          </div>
        ) : (
          <>
            {/* Main Content */}
            <div className="mt-5 flex min-h-0 flex-1 gap-6">
              {/* Left - Image */}
              <div className="flex w-[45%] min-w-0 flex-col">
                <div className="mb-3 flex items-center justify-between">
                  {/* <p className="font-lufga text-[16px] font-medium text-[#191919]">
                    Image {currentDocument?.sequence || currentPage + 1}
                  </p> */}

                  <p className="font-lufga text-[14px] text-[#666666]">
                    {currentPage + 1} / {rejectedDocuments.length}
                  </p>
                </div>

                <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-[16px] border border-[#E4E4E4] bg-[#F9F9F9] p-4">
                  {currentDocument?.documentUrl ? (
                    <img
                      src={currentDocument.documentUrl}
                      alt={`Reference document ${currentPage + 1}`}
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <p className="font-lufga text-[14px] text-[#797979]">
                      No image found
                    </p>
                  )}
                </div>
              </div>

              {/* Right - Rejection Reasons */}
              <div className="flex w-[55%] min-w-0 flex-col">
                <p className="mb-3 font-lufga text-[17px] font-medium text-[#191919]">
                  Rejection Reasons
                </p>

                <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
                  {rejectionReasons.map((item, index) => (
                    <div
                      key={`${item?.rejectionDate}-${index}`}
                      className="flex-shrink-0 rounded-[8px] bg-[#F2FCF9] p-3"
                    >
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <p className="font-lufga text-[14px] font-medium text-[#191919]">
                          Reason {index + 1}
                        </p>

                        <p className="flex-shrink-0 font-lufga text-[12px] text-[#777777]">
                          {formatDate(item?.rejectionDate)}
                        </p>
                      </div>

                      <p className="break-words whitespace-normal font-lufga text-[14px] leading-5 text-[#333333]">
                        {item?.rejectionReason}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Pagination */}
            {rejectedDocuments.length > 1 && (
              <div className="mt-5 flex flex-shrink-0 items-center justify-center gap-4">
                <button
                  type="button"
                  disabled={!hasPrevious}
                  onClick={() => setCurrentPage((prev) => prev - 1)}
                  className="rounded-full border border-[#017C57] bg-white px-5 py-2 font-lufga text-[14px] text-[#017C57] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Previous
                </button>

                <span className="font-lufga text-[14px] text-[#191919]">
                  {currentPage + 1} / {rejectedDocuments.length}
                </span>

                <button
                  type="button"
                  disabled={!hasNext}
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                  className="rounded-full border border-[#017C57] bg-white px-5 py-2 font-lufga text-[14px] text-[#017C57] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ReferenceRejectionHistoryModal;
